import { take, call, put } from 'redux-saga/effects'
import * as types from '../../lib/actionTypes.es6'
import {
  // Generic Products
  getProducts,
  createProduct,
  getProduct,
  editProduct,

  // Generic Product Packages
  createProductPackage,
  deleteProductPackage,
  updateProductPackage,
  getProductPackages,

  // Generic Product Package Benefits
  createProductPackageBenefit,
  getProductPackageBenefits,
  updateProductPackageBenefit,
  deleteProductPackageBenefit,
  getProductAddons,
  createProductAddon,
  updateProductAddon,
  deleteProductAddon,
  createCashbackConfiguration,
  updateCashbackConfiguration,
  deleteCashbackConfiguration,
  getCashbackConfigurations,
  createAgeValidationRule,
  updateAgeValidationRule,
  deleteAgeValidationRule,
  getAgeValidationRules,
  createProductAutoRenewalConfiguration,
  updateProductAutoRenewalConfiguration,
  deleteProductAutoRenewalConfiguration,
  getProductAutoRenewalConfiguration,

  // AI Drafting
  getDraftSignedUrl,
  putDraftDocumentToS3,
  importDraftDocument,
  pollDraftJob,
  getPartnerDrafts,
  getDraftDetail,
  importDraftProduct,
  evaluateProductQuote,
  compareQuotations,

  // Rating Nodes
  getProductRatingNodes,
  createProductRatingNode,
  updateProductRatingNode,
  deleteProductRatingNode,

  // Rating Tables
  getRatingTables,
  createRatingTable,
  updateRatingTable,
  activateRatingTable,
  deactivateRatingTable,
  deleteRatingTable,

  // Rating Bulk Import
  getRatingImportTemplate,
  validateRatingWorkbook,
  importRatingWorkbook,

  // Rating Snapshots
  getRatingSnapshots,

  // Change Requests
  getPartnerChangeRequests,
  approveChangeRequest,
  rejectChangeRequest
} from '../../lib/requests.es6'
import * as actions from './actions.es6'
import { unAuthorizedNotification } from '../../lib/access.es6'
import { GAloggerException } from '../../lib/monitoring.es6'
import { formatErrorMessage, downloadBlobReports } from '../../lib/utils.es6'
import { handleMutationResponse } from '../../lib/mutation-response.es6'

export function* getAllProductsRequestWatcher() {
  for (;;) {
    const request = yield take(types.VIEW_ALL_PRODUCTS_REQUEST)
    try {
      const response = yield call(getProducts, request.payload)
      yield put(actions.getAllProductsSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.getAllProductsFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* createProductRequestWatcher() {
  for (;;) {
    const request = yield take(types.CREATE_PRODUCT_REQUEST)
    try {
      const response = yield call(createProduct, request.payload)
      yield put(actions.createProductSuccess(response.data))

      yield put(
        actions.getAllProductsRequest({
          partnerGuid: request.payload.partnerGuid,
          activeOnly: false,
        })
      )
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.createProductFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* getProductRequestWatcher() {
  for (;;) {
    const request = yield take(types.VIEW_PRODUCT_REQUEST)
    try {
      const response = yield call(getProduct, request.payload)
      yield put(actions.getProductSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.getProductFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* editProductRequestWatcher() {
  for (;;) {
    const request = yield take(types.EDIT_PRODUCT_REQUEST)
    try {
      const response = yield call(editProduct, request.payload)
      yield put(actions.editProductSuccess(response.data))

      yield put(actions.getProductRequest(request.payload.product_guid))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.editProductFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* createProductPackageRequestWatcher() {
  for (;;) {
    const request = yield take(types.CREATE_PRODUCT_PACKAGE_REQUEST)
    try {
      // PackageDto has no premium_amount_in_cents field — package premium is
      // rating-node-driven. If the form collected a flat amount, materialize
      // it as a _package/premium flat_rate RatingNode first and link its guid,
      // instead of sending the amount on the package itself.
      const { premium_amount_in_cents, ...packagePayload } = request.payload
      let premiumChangeRequestGuid = null
      let createdRatingNode = false
      if (premium_amount_in_cents != null) {
        const nodeResponse = yield call(createProductRatingNode, {
          product_guid: request.payload.product_guid,
          package_code: request.payload.code,
          benefit_code: '_package',
          node_type: 'flat_rate',
          applies_to: 'premium',
          base_amount_in_cents: premium_amount_in_cents,
          label: `${request.payload.name} Premium`,
        })
        // RatingNode creation is unconditionally exempt from the approval
        // gate on the backend (product-configuration#27) — this is normally
        // a created node now, not a change request. It still CAN come back
        // as a change request for update flows or partners predating that
        // rollout, so this branch stays: nothing to link yet in that case.
        // The evaluator's (package_code, '_package') sentinel match picks
        // the node up automatically once approved, with no FK needed — but
        // the UI still has to say so, rather than silently going quiet on
        // the premium the user just typed in.
        if (nodeResponse.data && nodeResponse.data.guid) {
          packagePayload.premium_rating_node_guid = nodeResponse.data.guid
          createdRatingNode = true
        } else if (nodeResponse.data && nodeResponse.data.change_request_guid) {
          premiumChangeRequestGuid = nodeResponse.data.change_request_guid
        }
      }
      const response = yield call(createProductPackage, packagePayload)
      yield put(actions.createProductPackageSuccess({
        ...response.data,
        // Client-side-only annotation, never sent anywhere — tells the UI
        // the premium isn't live yet. See createProductPackageRequestWatcher.
        premium_change_request_guid: premiumChangeRequestGuid,
      }))
      // Refresh the product data after successful package creation
      if (request.payload.product_guid) {
        yield put(actions.getProductRequest(request.payload.product_guid))
      }
      // The premium RatingNode created above isn't in the page's ratingNodes
      // collection yet — without this, the picker falls back to rendering
      // its bare guid instead of a label until the next unrelated refetch.
      if (createdRatingNode && request.payload.product_guid) {
        yield put(actions.getProductRatingNodesRequest(request.payload.product_guid))
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.createProductPackageFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* deleteProductPackageRequestWatcher() {
  for (;;) {
    const request = yield take(types.DELETE_PRODUCT_PACKAGE_REQUEST)
    try {
      const response = yield call(deleteProductPackage, request.payload)
      yield put(actions.deleteProductPackageSuccess(response.data))
      // Refresh the product data after successful package deletion
      if (request.payload.product_guid) {
        yield put(actions.getProductRequest(request.payload.product_guid))
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.deleteProductPackageFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* updateProductPackageRequestWatcher() {
  for (;;) {
    const request = yield take(types.UPDATE_PRODUCT_PACKAGE_REQUEST)
    try {
      // Premium is edited via the dedicated Premium Rating Node picker
      // (handlePackageRatingChange), never through this form — PackageDto has
      // no premium_amount_in_cents field, so drop it if somehow still present.
      const { premium_amount_in_cents, ...packagePayload } = request.payload
      const response = yield call(updateProductPackage, packagePayload)
      yield put(actions.updateProductPackageSuccess(response.data))
      // Refresh the product data after successful package update
      if (request.payload.product_guid) {
        yield put(actions.getProductRequest(request.payload.product_guid))
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.updateProductPackageFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* getProductPackagesRequestWatcher() {
  for (;;) {
    const request = yield take(types.GET_PRODUCT_PACKAGES_REQUEST)
    try {
      const response = yield call(getProductPackages, request.payload)
      yield put(actions.getProductPackagesSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.getProductPackagesFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* createProductPackageBenefitRequestWatcher() {
  for (;;) {
    const request = yield take(types.CREATE_PRODUCT_PACKAGE_BENEFITS_REQUEST)
    try {
      const response = yield call(createProductPackageBenefit, request.payload)
      yield put(actions.createProductPackageBenefitSuccess(response.data))
      // Refresh the product data after successful benefit creation
      if (request.payload.product_guid) {
        yield put(actions.getProductRequest(request.payload.product_guid))
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.createProductPackageBenefitFailure(errorMessage))

      try { GAloggerException(e); } catch (_) {}
      try { unAuthorizedNotification(e); } catch (_) {}
    }
  }
}

// Benefit pricing wizard's "Per rule" orchestration — materializes a
// RatingTable + RatingNode per target (Cover/Waiting Period/Premium) that
// chose "Per rule", then hands off to the existing benefit-create watcher
// with the resulting FKs merged in. See
// docs/2026-09-04-benefit-pricing-wizard-design.md §5.1/§5.5.
//
// request.payload:
//   product_guid, package_guid — routing
//   targets: [{ key, fkField, tablePayload, nodePayload, tableGuid?, nodeGuid? }]
//     one entry per rating target using "Per rule"; a target already carrying
//     tableGuid/nodeGuid (from a prior failed attempt's progress) is treated
//     as already done and skipped, so a retry only redoes what actually
//     failed rather than re-creating everything from scratch.
//   benefitPayload — everything else for the final createProductPackageBenefit
//     call (name, allowed_relationships, any flat-mode fields, etc.) — the
//     FK field for each target gets merged in once its node exists.
export function* createBenefitRatingChainRequestWatcher() {
  for (;;) {
    const request = yield take(types.CREATE_BENEFIT_RATING_CHAIN_REQUEST)
    const { product_guid, package_guid, targets, benefitPayload } = request.payload
    // Shallow-copy each target so it can be mutated in place as its guids
    // land, without touching the caller's original objects.
    const progress = targets.map(t => Object.assign({}, t))
    try {
      for (let i = 0; i < progress.length; i++) {
        const target = progress[i]
        if (target.tableGuid && target.nodeGuid) continue

        if (!target.tableGuid) {
          const tableResponse = yield call(createRatingTable, target.tablePayload)
          target.tableGuid = tableResponse.data.guid
          yield put(actions.createBenefitRatingChainProgress({ targets: progress }))
        }

        if (!target.nodeGuid) {
          const nodeResponse = yield call(createProductRatingNode, Object.assign(
            {},
            target.nodePayload,
            {
              product_guid,
              tables: [{ rating_table_guid: target.tableGuid, order_index: 0 }]
            }
          ))
          // RatingNode creation is unconditionally exempt from the approval
          // gate (product-configuration#27), so this should always come back
          // with a guid, never a change_request_guid. Fail loudly instead of
          // silently carrying on with no FK to link if that ever changes.
          if (!nodeResponse.data || !nodeResponse.data.guid) {
            throw new Error(
              `Rating node for "${target.key}" did not create immediately ` +
              '(unexpected pending change request) — cannot continue.'
            )
          }
          target.nodeGuid = nodeResponse.data.guid
          yield put(actions.createBenefitRatingChainProgress({ targets: progress }))
        }
      }

      const finalPayload = Object.assign({}, benefitPayload, {
        package_guid,
        product_guid
      })
      progress.forEach(target => {
        finalPayload[target.fkField] = target.nodeGuid
      })

      yield put(actions.createBenefitRatingChainSuccess({ targets: progress }))
      // The table(s)/node(s) just created aren't in the Rating Tables/Rating
      // Nodes tabs' state yet — without this, they're invisible everywhere
      // (including this same benefit's own picker) until an unrelated
      // refetch happens to occur. Same gap as the Package-premium flow.
      yield put(actions.getRatingTablesRequest())
      yield put(actions.getProductRatingNodesRequest(product_guid))
      // Hand off to the existing, already-running benefit-create watcher —
      // reuses its success/failure handling and post-create product refresh
      // rather than duplicating them here.
      yield put(actions.createProductPackageBenefitRequest(finalPayload))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      // Report whatever succeeded before the failure so the wizard can merge
      // it into a retry's `targets` and skip re-creating it (§5.5).
      yield put(actions.createBenefitRatingChainProgress({ targets: progress }))
      yield put(actions.createBenefitRatingChainFailure(errorMessage))

      try { GAloggerException(e); } catch (_) {}
      try { unAuthorizedNotification(e); } catch (_) {}
    }
  }
}

// Edit-mode counterpart (§9.3) — a mix of create/update actions per target,
// since an existing benefit's Cover/Waiting/Premium can each be transitioning
// differently on the same edit (e.g. Cover's rules changed while Waiting
// Period just switched All → Per rule). "Cleared" targets (Per rule → All)
// need no rating call at all — the wizard already nulled that FK directly in
// benefitPayload before dispatching this.
//
// request.payload:
//   product_guid, package_guid, benefit_guid — routing
//   createTargets — same shape as createBenefitRatingChainRequestWatcher's
//     targets (a target newly switching to "Per rule")
//   updateTargets — [{ key, tableGuid, tableVersion, tablePayload: {segments, cells} }]
//     (a target staying "Per rule", rules possibly changed) — the FK for
//     these is already set in benefitPayload by the wizard (it doesn't change)
//   benefitPayload — everything for the final updateProductPackageBenefit call
export function* updateBenefitRatingChainRequestWatcher() {
  for (;;) {
    const request = yield take(types.UPDATE_BENEFIT_RATING_CHAIN_REQUEST)
    const { product_guid, package_guid, benefit_guid, createTargets, updateTargets, benefitPayload } = request.payload
    const createProgress = (createTargets || []).map(t => Object.assign({}, t))
    const pending = []
    try {
      for (let i = 0; i < createProgress.length; i++) {
        const target = createProgress[i]
        if (target.tableGuid && target.nodeGuid) continue

        if (!target.tableGuid) {
          const tableResponse = yield call(createRatingTable, target.tablePayload)
          target.tableGuid = tableResponse.data.guid
          yield put(actions.updateBenefitRatingChainProgress({ createTargets: createProgress }))
        }

        if (!target.nodeGuid) {
          const nodeResponse = yield call(createProductRatingNode, Object.assign(
            {},
            target.nodePayload,
            {
              product_guid,
              tables: [{ rating_table_guid: target.tableGuid, order_index: 0 }]
            }
          ))
          if (!nodeResponse.data || !nodeResponse.data.guid) {
            throw new Error(
              `Rating node for "${target.key}" did not create immediately ` +
              '(unexpected pending change request) — cannot continue.'
            )
          }
          target.nodeGuid = nodeResponse.data.guid
          yield put(actions.updateBenefitRatingChainProgress({ createTargets: createProgress }))
        }
      }

      // Table updates are maker-checker gated (§9.4, unlike creation) — a
      // 202 here doesn't block the benefit save (the FK isn't changing
      // either way), it's just surfaced so the UI can show a pending banner
      // instead of implying the new rules already took effect.
      for (let i = 0; i < (updateTargets || []).length; i++) {
        const target = updateTargets[i]
        const response = yield call(updateRatingTable, {
          guid: target.tableGuid,
          version: target.tableVersion,
          payload: target.tablePayload
        })
        const mutation = handleMutationResponse(response)
        if (mutation.pending) {
          pending.push({ key: target.key, changeRequestGuid: mutation.changeRequestGuid })
        }
      }

      const finalPayload = Object.assign({}, benefitPayload, {
        benefit_guid,
        package_guid,
        product_guid
      })
      createProgress.forEach(target => {
        finalPayload[target.fkField] = target.nodeGuid
      })

      yield put(actions.updateBenefitRatingChainSuccess({ pending }))
      yield put(actions.getRatingTablesRequest())
      yield put(actions.getProductRatingNodesRequest(product_guid))
      yield put(actions.updateProductPackageBenefitRequest(finalPayload))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.updateBenefitRatingChainProgress({ createTargets: createProgress }))
      yield put(actions.updateBenefitRatingChainFailure(errorMessage))

      try { GAloggerException(e); } catch (_) {}
      try { unAuthorizedNotification(e); } catch (_) {}
    }
  }
}

export function* getProductPackageBenefitsRequestWatcher() {
  for (;;) {
    const request = yield take(types.GET_PRODUCT_PACKAGE_BENEFITS_REQUEST)
    try {
      const response = yield call(getProductPackageBenefits, request.payload)
      yield put(actions.getProductPackageBenefitsSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.getProductPackageBenefitsFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* updateProductPackageBenefitRequestWatcher() {
  for (;;) {
    const request = yield take(types.UPDATE_PRODUCT_PACKAGE_BENEFITS_REQUEST)
    try {
      const response = yield call(updateProductPackageBenefit, request.payload)
      yield put(actions.updateProductPackageBenefitSuccess(response.data))
      // Refresh the product data after successful benefit update
      if (request.payload.product_guid) {
        yield put(actions.getProductRequest(request.payload.product_guid))
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.updateProductPackageBenefitFailure(errorMessage))

      try { GAloggerException(e); } catch (_) {}
      try { unAuthorizedNotification(e); } catch (_) {}
    }
  }
}

export function* deleteProductPackageBenefitRequestWatcher() {
  for (;;) {
    const request = yield take(types.DELETE_PRODUCT_PACKAGE_BENEFITS_REQUEST)
    try {
      const response = yield call(deleteProductPackageBenefit, request.payload)
      yield put(actions.deleteProductPackageBenefitSuccess(response.data))
      // Refresh the product data after successful benefit deletion
      if (request.payload.product_guid) {
        yield put(actions.getProductRequest(request.payload.product_guid))
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.deleteProductPackageBenefitFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* getProductAddonsRequestWatcher() {
  for (;;) {
    const request = yield take(types.GET_PRODUCT_ADDONS_REQUEST)
    try {
      const response = yield call(getProductAddons, request.payload)
      yield put(actions.getProductAddonsSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.getProductAddonsFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* createProductAddonRequestWatcher() {
  for (;;) {
    const request = yield take(types.CREATE_PRODUCT_ADDON_REQUEST)
    try {
      const response = yield call(createProductAddon, request.payload)
      yield put(actions.createProductAddonSuccess(response.data))
      // Refresh the product data after successful addon creation
      if (request.payload.product_guid) {
        yield put(actions.getProductRequest(request.payload.product_guid))
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.createProductAddonFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* updateProductAddonRequestWatcher() {
  for (;;) {
    const request = yield take(types.UPDATE_PRODUCT_ADDON_REQUEST)
    try {
      const response = yield call(updateProductAddon, request.payload)
      yield put(actions.updateProductAddonSuccess(response.data))
      // Refresh the product data after successful addon update
      if (request.payload.product_guid) {
        yield put(actions.getProductRequest(request.payload.product_guid))
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.updateProductAddonFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* deleteProductAddonRequestWatcher() {
  for (;;) {
    const request = yield take(types.DELETE_PRODUCT_ADDON_REQUEST)
    try {
      const response = yield call(deleteProductAddon, request.payload)
      yield put(actions.deleteProductAddonSuccess(response.data))
      // Refresh the product data after successful addon deletion
      if (request.payload.product_guid) {
        yield put(actions.getProductRequest(request.payload.product_guid))
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.deleteProductAddonFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* createCashbackConfigRequestWatcher() {
  for (;;) {
    const request = yield take(types.CREATE_CASHBACK_CONFIG_REQUEST)
    try {
      const response = yield call(createCashbackConfiguration, request.payload)
      yield put(actions.createCashBackConfigSuccess(response.data))
      // Refresh the product data after successful cashback creation
      if (request.payload.product_guid) {
        yield put(actions.getProductRequest(request.payload.product_guid))
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.createCashBackConfigFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* updateCashbackConfigRequestWatcher() {
  for (;;) {
    const request = yield take(types.UPDATE_CASHBACK_CONFIG_REQUEST)
    try {
      const response = yield call(updateCashbackConfiguration, request.payload)
      yield put(actions.updateCashBackConfigSuccess(response.data))
      // Refresh the product data after successful cashback update
      if (request.payload.product_guid) {
        yield put(actions.getProductRequest(request.payload.product_guid))
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.updateCashBackConfigFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* deleteCashbackConfigRequestWatcher() {
  for (;;) {
    const request = yield take(types.DELETE_CASHBACK_CONFIG_REQUEST)
    try {
      const response = yield call(deleteCashbackConfiguration, request.payload)
      yield put(actions.deleteCashBackConfigSuccess(response.data))
      // Refresh the product data after successful cashback deletion
      if (request.payload.product_guid) {
        yield put(actions.getProductRequest(request.payload.product_guid))
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.deleteCashBackConfigFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* getCashbackConfigRequestWatcher() {
  for (;;) {
    const request = yield take(types.GET_CASHBACK_CONFIGS_REQUEST)
    try {
      const response = yield call(getCashbackConfigurations, request.payload)
      yield put(actions.getCashBackConfigsSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.getCashBackConfigsFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* createAgeValidationRuleRequestWatcher() {
  for (;;) {
    try {
      const request = yield take(types.CREATE_AGE_VALIDATION_RULE_REQUEST)
      console.log(
        'Received CREATE_AGE_VALIDATION_RULE_REQUEST with payload:',
        request.payload
      )

      try {
        const response = yield call(createAgeValidationRule, request.payload)
        console.log('Age validation rule created successfully:', response.data)
        yield put(actions.createAgeValidationRuleSuccess(response.data))
        // Refresh the product data after successful age rule creation
        if (request.payload.product_guid) {
          yield put(actions.getProductRequest(request.payload.product_guid))
        }
      } catch (e) {
        console.error('Error creating age validation rule:', e)
        const errorMessage = formatErrorMessage(e)

        console.log(
          'Dispatching age validation rule failure with message:',
          errorMessage
        )
        yield put(actions.createAgeValidationRuleFailure(errorMessage))

        GAloggerException(e)
        unAuthorizedNotification(e)
      }
    } catch (outerError) {
      console.error(
        'Outer error in createAgeValidationRuleRequestWatcher:',
        outerError
      )
      // Don't break the saga loop even if there's an unexpected error
    }
  }
}

export function* updateAgeValidationRuleRequestWatcher() {
  for (;;) {
    const request = yield take(types.UPDATE_AGE_VALIDATION_RULE_REQUEST)
    try {
      const response = yield call(updateAgeValidationRule, request.payload)
      yield put(actions.updateAgeValidationRuleSuccess(response.data))
      // Refresh the product data after successful age rule update
      if (request.payload.product_guid) {
        yield put(actions.getProductRequest(request.payload.product_guid))
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.updateAgeValidationRuleFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* deleteAgeValidationRuleRequestWatcher() {
  for (;;) {
    const request = yield take(types.DELETE_AGE_VALIDATION_RULE_REQUEST)
    try {
      const response = yield call(deleteAgeValidationRule, request.payload)
      yield put(actions.deleteAgeValidationRuleSuccess(response.data))
      // Refresh the product data after successful age rule deletion
      if (request.payload.product_guid) {
        yield put(actions.getProductRequest(request.payload.product_guid))
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.deleteAgeValidationRuleFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* getAgeValidationRulesRequestWatcher() {
  for (;;) {
    const request = yield take(types.GET_AGE_VALIDATION_RULES_REQUEST)
    try {
      const response = yield call(getAgeValidationRules, request.payload)
      yield put(actions.getAgeValidationRulesSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.getAgeValidationRulesFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* getProductAutoRenewalConfigRequestWatcher() {
  for (;;) {
    const request = yield take(types.GET_PRODUCT_AUTO_RENEWAL_CONFIG_REQUEST)
    try {
      const response = yield call(
        getProductAutoRenewalConfiguration,
        request.payload
      )
      yield put(actions.getProductAutoRenewalConfigSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.getProductAddonsFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* updateProductAutoRenewalConfigRequestWatcher() {
  for (;;) {
    const request = yield take(types.UPDATE_PRODUCT_AUTO_RENEWAL_CONFIG_REQUEST)
    try {
      const response = yield call(
        updateProductAutoRenewalConfiguration,
        request.payload
      )
      yield put(actions.updateProductAutoRenewalConfigSuccess(response.data))
      // Refresh the product data after successful auto-renewal config update
      if (request.payload.product_guid) {
        yield put(actions.getProductRequest(request.payload.product_guid))
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.updateProductAutoRenewalConfigFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* createProductAutoRenewalConfigRequestWatcher() {
  for (;;) {
    const request = yield take(types.CREATE_PRODUCT_AUTO_RENEWAL_CONFIG_REQUEST)
    try {
      const response = yield call(
        createProductAutoRenewalConfiguration,
        request.payload
      )
      yield put(actions.createProductAutoRenewalConfigSuccess(response.data))
      // Refresh the product data after successful auto-renewal config creation
      if (request.payload.product_guid) {
        yield put(actions.getProductRequest(request.payload.product_guid))
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.createProductAutoRenewalConfigFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* deleteProductAutoRenewalConfigRequestWatcher() {
  for (;;) {
    const request = yield take(types.DELETE_PRODUCT_AUTO_RENEWAL_CONFIG_REQUEST)
    try {
      const response = yield call(
        deleteProductAutoRenewalConfiguration,
        request.payload
      )
      yield put(actions.deleteProductAutoRenewalConfigSuccess(response.data))
      // Refresh the product data after successful auto-renewal config deletion
      if (request.payload.product_guid) {
        yield put(actions.getProductRequest(request.payload.product_guid))
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.deleteProductAutoRenewalConfigFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* uploadDocumentRequestWatcher() {
  for (;;) {
    const request = yield take(types.UPLOAD_DOCUMENT_REQUEST)
    try {
      const file = request.payload
      // Signed-URL pattern: ask the backend for a presigned S3 URL, PUT the
      // file straight to S3, then hand the server-issued key to /import.
      const signed = yield call(getDraftSignedUrl, file.name)
      const { signedUrl, bucketKey } = signed.data
      yield call(putDraftDocumentToS3, signedUrl, file)
      const imported = yield call(importDraftDocument, bucketKey)
      yield put(actions.uploadDocumentSuccess(imported.data.job_id))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.uploadDocumentFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* pollDraftJobRequestWatcher() {
  for (;;) {
    const request = yield take(types.POLL_DRAFT_JOB_REQUEST)
    try {
      const response = yield call(pollDraftJob, request.payload)
      // Backend job status: Pending (running) | Ready (result attached) | Failed.
      const { status, result, error } = response.data
      if (status === 'Ready') {
        yield put(actions.pollDraftJobSuccess(result))
      } else if (status === 'Failed') {
        yield put(
          actions.pollDraftJobFailure(error || 'Draft generation failed')
        )
      } else {
        // Pending — not a failure; signal the dialog to schedule the next poll.
        yield put(actions.pollDraftJobProcessing())
      }
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.pollDraftJobFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* fetchPartnerDraftsRequestWatcher() {
  for (;;) {
    yield take(types.FETCH_PARTNER_DRAFTS_REQUEST)
    try {
      const response = yield call(getPartnerDrafts)
      yield put(actions.fetchPartnerDraftsSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.fetchPartnerDraftsFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* reviewDraftRequestWatcher() {
  for (;;) {
    const request = yield take(types.REVIEW_DRAFT_REQUEST)
    try {
      const response = yield call(getDraftDetail, request.payload)
      yield put(actions.reviewDraftSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.reviewDraftFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* importDraftProductRequestWatcher() {
  for (;;) {
    const request = yield take(types.IMPORT_DRAFT_PRODUCT_REQUEST)
    try {
      const response = yield call(importDraftProduct, request.payload)
      yield put(actions.importDraftProductSuccess(response.data.product))
      // Refresh the drafts table so the row flips to Imported.
      yield put(actions.fetchPartnerDraftsRequest())
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.importDraftProductFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* evaluateProductQuoteRequestWatcher() {
  for (;;) {
    const request = yield take(types.EVALUATE_PRODUCT_QUOTE_REQUEST)
    try {
      const { productGuid, payload } = request.payload
      const response = yield call(evaluateProductQuote, productGuid, payload)
      yield put(actions.evaluateProductQuoteSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.evaluateProductQuoteFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* compareQuotationsRequestWatcher() {
  for (;;) {
    const request = yield take(types.COMPARE_QUOTATIONS_REQUEST)
    try {
      const response = yield call(compareQuotations, request.payload)
      yield put(actions.compareQuotationsSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.compareQuotationsFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* getProductRatingNodesRequestWatcher() {
  for (;;) {
    const request = yield take(types.GET_PRODUCT_RATING_NODES_REQUEST)
    try {
      const response = yield call(getProductRatingNodes, request.payload)
      yield put(actions.getProductRatingNodesSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.getProductRatingNodesFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* createProductRatingNodeRequestWatcher() {
  for (;;) {
    const request = yield take(types.CREATE_PRODUCT_RATING_NODE_REQUEST)
    try {
      const response = yield call(createProductRatingNode, request.payload)
      yield put(actions.createProductRatingNodeSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.createProductRatingNodeFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* updateProductRatingNodeRequestWatcher() {
  for (;;) {
    const request = yield take(types.UPDATE_PRODUCT_RATING_NODE_REQUEST)
    try {
      const response = yield call(
        updateProductRatingNode,
        request.payload.node_guid,
        request.payload.node
      )
      yield put(actions.updateProductRatingNodeSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.updateProductRatingNodeFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* deleteProductRatingNodeRequestWatcher() {
  for (;;) {
    const request = yield take(types.DELETE_PRODUCT_RATING_NODE_REQUEST)
    try {
      yield call(deleteProductRatingNode, request.payload.node_guid)
      yield put(
        actions.deleteProductRatingNodeSuccess(request.payload.node_guid)
      )
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.deleteProductRatingNodeFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

// ── Rating Tables ────────────────────────────────────────────────────────────
export function* getRatingTablesRequestWatcher() {
  for (;;) {
    yield take(types.GET_RATING_TABLES_REQUEST)
    try {
      const response = yield call(getRatingTables)
      yield put(actions.getRatingTablesSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.getRatingTablesFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* ratingTableActionRequestWatcher() {
  for (;;) {
    const request = yield take(types.RATING_TABLE_ACTION_REQUEST)
    const { guid, action } = request.payload
    try {
      if (action === 'activate') {
        yield call(activateRatingTable, guid)
      } else if (action === 'deactivate') {
        yield call(deactivateRatingTable, guid)
      } else if (action === 'delete') {
        yield call(deleteRatingTable, guid)
      }
      yield put(actions.ratingTableActionSuccess(action))
      // Refresh the list so the change (or a submitted change request) reflects.
      yield put(actions.getRatingTablesRequest())
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.ratingTableActionFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* saveRatingTableRequestWatcher() {
  for (;;) {
    const request = yield take(types.SAVE_RATING_TABLE_REQUEST)
    const { guid, version, table } = request.payload
    try {
      let response
      if (guid) {
        response = yield call(updateRatingTable, { guid, version, payload: table })
      } else {
        response = yield call(createRatingTable, table)
      }
      // Creating a table is unconditionally exempt from the approval gate
      // (product-configuration#27) and answers 200/201 immediately; editing
      // an existing one still respects the partner's product_maker_checker
      // and can come back 202 pending. Branch via the shared helper rather
      // than assuming either, so "saved" only means saved.
      const { pending, changeRequestGuid } = handleMutationResponse(response)
      yield put(actions.saveRatingTableSuccess({ pending, changeRequestGuid }))
      yield put(actions.getRatingTablesRequest())
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.saveRatingTableFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

// ── Change Requests ──────────────────────────────────────────────────────────
export function* getChangeRequestsRequestWatcher() {
  for (;;) {
    const request = yield take(types.GET_CHANGE_REQUESTS_REQUEST)
    try {
      const status = request.payload && request.payload.status
      const domain = request.payload && request.payload.domain
      const response = yield call(getPartnerChangeRequests, status, domain)
      yield put(actions.getChangeRequestsSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.getChangeRequestsFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* resolveChangeRequestRequestWatcher() {
  for (;;) {
    const request = yield take(types.RESOLVE_CHANGE_REQUEST_REQUEST)
    const { guid, decision, reason, domain } = request.payload
    try {
      if (decision === 'approve') {
        yield call(approveChangeRequest, { guid, reason })
      } else {
        yield call(rejectChangeRequest, { guid, reason })
      }
      yield put(actions.resolveChangeRequestSuccess(decision))
      // Re-fetch with whatever domain filter (Product/Rating) was active in
      // the screen, so approving/rejecting from the Rating queue doesn't
      // silently flip the view back to "all".
      yield put(actions.getChangeRequestsRequest({ domain }))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.resolveChangeRequestFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

// ── Rating Bulk Import ───────────────────────────────────────────────────────

export function* downloadRatingTemplateRequestWatcher() {
  for (;;) {
    const request = yield take(types.DOWNLOAD_RATING_TEMPLATE_REQUEST)
    const productGuid = request.payload
    try {
      const response = yield call(getRatingImportTemplate, productGuid)
      downloadBlobReports(
        response.data,
        `rating-import-${productGuid}.xlsx`
      )
      yield put(actions.downloadRatingTemplateSuccess())
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.downloadRatingTemplateFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* validateRatingWorkbookRequestWatcher() {
  for (;;) {
    const request = yield take(types.VALIDATE_RATING_WORKBOOK_REQUEST)
    const { productGuid, file } = request.payload
    try {
      const response = yield call(validateRatingWorkbook, productGuid, file)
      yield put(actions.validateRatingWorkbookSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.validateRatingWorkbookFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

// A clean import always answers 202 — rating changes are unconditionally
// four-eyes gated, so this never "applies" inline. Success here means "queued
// as a change request", not "materialized".
export function* importRatingWorkbookRequestWatcher() {
  for (;;) {
    const request = yield take(types.IMPORT_RATING_WORKBOOK_REQUEST)
    const { productGuid, file } = request.payload
    try {
      const response = yield call(importRatingWorkbook, productGuid, file)
      yield put(actions.importRatingWorkbookSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.importRatingWorkbookFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

// ── Rating Snapshots ─────────────────────────────────────────────────────────

export function* getRatingSnapshotsRequestWatcher() {
  for (;;) {
    const request = yield take(types.GET_RATING_SNAPSHOTS_REQUEST)
    const productGuid = request.payload
    try {
      const response = yield call(getRatingSnapshots, productGuid)
      yield put(actions.getRatingSnapshotsSuccess(response.data))
    } catch (e) {
      const errorMessage = formatErrorMessage(e)
      yield put(actions.getRatingSnapshotsFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}
